import { DataSet } from './DisplayInterfaces';
import { ChartConfiguration, ChartType } from 'chart.js';
import { IMultiSelectOption } from '../components/platform-multiselect';
// Shared widget configuration definition for ALL widgets
export interface WidgetConfiguration {

    // Unique name
    name: string;
    displayName?: string;
    adminRequired?: boolean;
    description: string;
    area?: string;

    // Information about who to contact for support if there are issues with the widget in the app
    supportMessage: string;

    // Used at runtime to determine which widget to load up.
    componentType: string;

    // Free section for adding whatever configuration the widget needs. When it is created in the group
    // display component, this section will be made available to it.
    config?: any;

    dataConsumerConfiguration: DataConsumerConfiguration;

    // if they choose to associate multiple data policies to one widget
    // we'll set the dataConsumerConfiguration propery to null
    // and create a list in this property..  requires widget designed handling (loading) of each data policy
    // this just enables web-platform-library to be the centeralized service for those data loads
    dataConsumerConfigurations?: DataConsumerConfiguration[];

    // See interface definition
    contextMatchingRules: ContextMatchingRule[];
    modified?: boolean;
    unSavedData?: boolean;
    dateModified?: string;
    // allow widget to run as an app singleton, removes grouping and other layout features, changes title.
    widgetAsApp?: boolean;
    // allow widget to be tagged with the array of string tags without dulpicated.
    tags?: string[];

}

// At any time the application can have a combination of context name/value pairs.  A list of these rules
//  is used to determine if the widget should be shown given the state of th context data
// For a widget to be displayed, the rule is as follows:
// 1. Context has ALL non-optional names
// 2. Context names have same value for all rules that specify a valueMatch
// 3. Context can have optional names
// 4. If context has names that aren't specified in the rule, the widget won't be displayed.
export interface ContextMatchingRule {
    contextName: string;
    valueMatch?: string;
    optional: boolean;
    historyItemsToKeep?: number;
}

export interface DataConsumerConfiguration {
    name?: string; // Data Consumer Config Name identifier
    dataPolicy: string;
    fixedData?: DataSet;
    // These parameter name/values are ALWAYS sent with the data request
    fixedParameters?: NameValue[];

    // Name is column name to sort, value can be '' or 'asc' to sort ascending or 'desc' to sort descending
    sortColumns?: NameValue[];

    // Mapping context name in app to query params in data source. Example:
    //  context is currently [{Source: Probe}, {Step: FPP.00}, {Design: L06A}]
    //  contextMapping is [{name: Step, value: ''}, {name: Design, value: 'DesignId'}]
    //  -> query params would be "?Step=FPP.00&DesignId=L06A"
    contextParameters?: NameValue[];
    // Optional rules for filtering the data that comes back
    filterConfiguration?: FilterRule[];
    overrideCacheRules?: CacheRules;
    dataGrouping?: DataGrouping;
    pivot?: Pivot;
    columns?: string[]; // Will be populated when data is returned at runtime
    overrideCustomHeaders?: NameValue[]
    directUri?: string; // if we're bypassing the middle layer this will get set when the policy is associated with the widget config, otherwise it will be null
    apiStoreAppKey?: string; // if we're bypassing the middle layer and using api gateway this will get set when the policy is associated with the widget config, otherwise it will be null
    httpMethod?: string; // if we're bypassing the middle layer this will get set when the policy is associated with the widget config, otherwise it will be null (default = get) 
    apiStoreSendMtAdfsOpenId?: boolean; // if true will also send in a x-mt-adfs-openid header with the users adfs access_token
}

export interface DataPolicy {
    name: string;
    supportMessage: string;
    supportGroup: string;
    supportNameList: string;
    sourceURL: string;
    CacheRules: CacheRules;
    DesignIdColumn?: string;
    fixedParameters?: NameValue[];
    columnRenameMap?: NameValue[];
    customHeaders?: NameValue[];
    dateModified?: string;
    useMiddleLayer?: boolean;
    dataFormat?: string;
    isAWebSocket?: boolean;
    httpMethod?: string;
    useAPIStore?: boolean;
    apiStoreAppKey?: string;
    apiStoreSendMtAdfsOpenId?: boolean;
    webSocketRegistrationMessage?: string;
}

export interface APIStoreTokenInfo {
    appKey: string
    token: string;
    expiration: Date;
}

export interface FilterRule {
    column: string;
    value?: string;
    contextValueName?: string;
    serverSide: boolean;
    // string operators: eq (default), startsWith, endsWith, contains,
    // case insensitive string operators ncEq, ncStartsWith, ncEndsWith, ncContains
    // number operators: ==, gt, lt, ge, le, ne
    operator?: string;
}

export interface CacheRules {
    period: string;    // 'hour|minute|day|workWeek|month|permanent|never'
    frequency?: number;
    targetMinute?: number;
    targetHour?: number;
    targetDay?: number;
    targetWorkWeekDay?: number;
}

export interface DataTableValueInfo {
    colIdx: number;
    rowIdx: number;
    column: string;
    row?: string[];
    value: string;
    event?: Event;
}

export interface TableButtonInfo {
    name: string;
    class: string;
    iconClass: string;
    text: string;
    toolTip?: string;
}

export interface TableRowSelectionButton {
    buttonName: string;
    row: string[];
    column: string[];
}
export type ActionButtonsPosition = 'start' | 'end';
export interface GenericTableConfiguration {
    hideHeaderRow?: boolean;
    sortAndfilterLocally?: boolean;
    buttonNames?: string[];
    buttonConfig?: ActionButtonConfig;
    buttonInfoList?: TableButtonInfo[];
    showFilterSortRow?: boolean;
    showGlobalFilter?: boolean;
    showPaginate?: boolean;
    showColumnDropdowns?: boolean;
    selectMode?: string; // default none.  options: 'row'.  (will soon support 'row','column','cell')
    pageSize?: number;
    tableColumnRules?: TableColumnRules[];
    showExportToCsv?: boolean;
    csvExportOptions?: CsvExportOptions;
    columnConfig?: ColumnConfig[]; // Extendable class for providing additional configuration options for each column
    columnHeaderConfigs?: ColumnHeaderConfigs[][]; // Extendable class for providing additional table header information. Builds multi row headers above normal columnConfig header.
    rowColorProp?: string; // the property / column name that contains an object for background and forground color of the entire table row
    currentPage?: number;
    showPageSizeSelector?: boolean; // whether the drop-down for page size is visible or not (default=true)
    showPageRecordsCount?: boolean; // whether the "Records 1 -10 of 27" text is shown (default=true)
    showFirstLastPageSelector?: boolean;  // wether the buttons for selecting the first & last page are shown (default=true)
    showPageInput?: boolean; // default true (whether the current page # is an input)
    showFilterToggleButton?: boolean; // whether the column filter button next to the global filter is visible (default=true)
    showGlobalFilterLabel?: boolean;  // whether the label for the global filter input is visible or not
    tableTitle?: string; // optional title to show up at the top next to the global filter.
    tdClass?: string;
    trClass?: string;
    thClass?: string;
    outerTrClass?: string;
    TableClasses?: string[];
}

export class ActionButtonConfig {
    buttonColumnName?: string;
    buttonPosition?: ActionButtonsPosition

}

export class Feedback {
    type: string;
    title: string;
    desc: string;
    userName: string;
    underConsideration: boolean;
    comment: Comment[];
    context: any;
    metaData: Map<string, string>;
}

export interface TableColumnRules {
    columnName: string; // Can be name or * for all columns
    hideColumn?: boolean;
    alternateColorOnValueChange?: boolean; // if true, as value changes going down column, bg color changes
    heatmapRules?: HeatmapRules;
    redmapRules?: HeatmapRules;
    greenmapRules?: HeatmapRules;
    showDropdown?: boolean;
    valueLink?: boolean;
    rowChangeBehavior?: RowChangeBehavior;
    maxTextLength?: number;
    showPareto?: boolean;
    paretoRules?: ParetoRules;
    isDate?: boolean;
    textAlign?: string; //left, center, right
}

export interface ColumnConfig {
    columnLabel: string; // the label (text) to show in the column header (prettier than the columnProp)
    columnLabelBgColor?: string; // specify column label header back ground color.
    columnLabelColor?: string; // specify column label font color.
    columnProp: string; // the column property to use for the data, or if coming from a dataset it is the column name (columnLabel=columnProp for DataSet inputs)
    sortClass?: string; // for sorting this is the intial sort direction if provided, or the current sort direction when toggled
    columnShowIcon?: boolean; // to show an icon instead of text (if the value is a font-aweomse icon class name)
    columnFixedWidth?: string; // specify in px to force a column to a set width
    columnSortByProp?: string; // allows you to specify a different property than the value shown for sorting a column (useful for durations and datetimes)
    columnHeaderFilterType?: string; // one of input, dropdown, or none - allows you to selectively change the type of column filter header element used.  If null defaults to input
    dropdownsVals?: string[]; // internal array generated on load of the availalbe values for drop-down options in the column header (if columnHeaderFilterType is dropdown)
    multiSelectVals?: IMultiSelectOption[];
    selectedValues?: string[];
}

export interface ColumnHeaderConfigs {
    headercolumnLabel: string; // the label (text) to show in the column header
    headercolumnFixedWidth?: string; // specify in px to force a column to a set width. Use this or the below colspan, but not both.
    headercolumnColSpan?: string; // specify column span.
    headercolumnLabelAlign?: string;
    headercolumnLabelBgColor?: string; // specify column header background-color.
    headercolumnLabelColor?: string; // specify column label font color.
}

export interface RowChangeBehavior {
    hideDuplicates?: boolean;
    addDividor?: boolean;
    whiteGray?: boolean;
    randomColor?: boolean;
    colorStringPallete?: string[]; //If exists, row change colors will cycle through this palette.
}

export interface HeatmapRules {
    low: string; // Can be 'min' or a number value as string. if min, uses min value of column
    high: string; // Can be 'max' or a number value as string. if max, uses max value of column
    columnsToInclude?: string[];
}

export interface ParetoRules {
    min: number;
    max: number;
}

export interface CsvExportOptions {
    separator?: string;
    fileName?: string;
    // TODO: Add this
    // onlyExportVisibleColumns: boolean;
}

export interface GenericFullChartConfiguration {

    // Specific labels on the x axis.  If these exist for the data, they will be the only labels displayed in the x axis
    xTickLabels?: string[];

    // Specific labels on the y axis.  If these exist for the data, they will be the only labels displayed in the y axis
    yTickLabels?: string[];

    // Columns to use for the X axis label - this will be the label across multiple series
    xAxisLabelColumns: string[];

    // Columns and their values used for the popopver tooltip when a user hovers over a chart point
    pointLabelColumns: string[];

    // Columns/values published as a filter parameter to the platform when the user clicks on a point
    filterColumns: string[];

    // This is the main means of defining the series that will be displayed.  A series has a one to one correlation to
    // a data set column.  Series can be grouped so that they can have different left/right axes labels
    seriesGroups: SeriesGroupConfig[];

    // Full Chart.js configuration set.  This allows you to set colors, data series types (bar, line, etc), scale options,
    // and anything else that can be configured. See the ChartConfiguration interface definition for a dive down into the
    // specifics
    chartConfig: ChartConfiguration;
}

// This is an older and much simpler configuration interface for the generic chart component.  It doesn't allow for the flexibility
// of the GenericFullChartConfiguration interface but is less complicated.
export interface GenericChartConfiguration {

    labelSources?: NameValue[];
    seriesGroups: SeriesGroupConfig[];

    // For tables or charts.  Name = series or column name.  Value = Context name
    // If this exists and user clicks on a point in the graph or a cell on a table, the
    // generic widget will notify the app of an addinal context where the name = context name and value =
    // value of point or cell clicked.
    clickMap?: NameValue[];

}

export interface SeriesGroupConfig {
    scalePosition: string;
    id: string;
    series: SeriesConfig[];
}

export interface SeriesConfig {
    // Series name displayed
    name: string;
    // Column that is source of data
    sourceColumn: string;

    // For scatter charts, sourceColumn is X and this is Y
    sourceColumnY?: string;
    // Type of chart (line, bar)
    type: ChartType;
    // type: ChartType | string;
}

export interface NameValue {
    name: string;
    value: string;
}

export interface NameIndex {
    name: string;
    index: number;
}

export interface DataGrouping {
    serverSide?: boolean;
    // Group data by these column
    groupBy: string[];
    // Aggregate by these columns.  Name = column name, Value = aggregate operator (sum, avg, count, min, max, others?)
    aggregations: Aggregation[];
}

export interface Aggregation {
    name: string;
    operation: string;
    rename: string;
    separator?: string;
}

export interface Pivot {
    name: string;
    operation: string;
    rename: string;
}

export interface ManagedDataReply {
    CacheData: CacheDataInfo;
    AppliedDataPolicy: DataPolicy;
}

export interface CacheDataInfo {
    MinutesRemaining: number;
    RetrievedFromCache: boolean;
    key: string;
    Payload: any;
    CacheDate: string;
}
